import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GrowlBrokerService, GrowlType } from '@dsivd/prestations-ng';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

import { CadorReference } from '../../model/cador-reference';
import { LibelleConfigurable } from '../../model/libelle-configurable';

export const REFCADOR_BASE_URL = 'api/refcador/';

@Injectable({
    providedIn: 'root'
})
export class RefCadorService {

    static CADOR_CODE_CONTACT_BENEFICIAIRE = 'Bénéficiaire';
    static CADOR_CODE_NON_RESIDENT_CANTON = 'Non résident canton Vaud';
    static CADOR_CODE_CONTACT_DEMANDEUR = 'Bénéficiaire';

    private initiators: CadorReference[];
    private readonly initiatorsObservable: Observable<CadorReference[]>;

    private projectTypes: CadorReference[];
    private readonly projectTypesObservable: Observable<CadorReference[]>;

    private relations: CadorReference[];
    private readonly relationsObservable: Observable<CadorReference[]>;

    private contactPreferences: CadorReference[];
    private readonly contactPreferencesObservable: Observable<CadorReference[]>;

    private situations: CadorReference[];
    private readonly situationsObservable: Observable<CadorReference[]>;

    private levelOfEducations: CadorReference[];
    private readonly levelOfEducationsObservable: Observable<CadorReference[]>;

    private libelles: LibelleConfigurable[];
    private readonly libellesObservable: Observable<LibelleConfigurable[]>;

    private typeActivities: CadorReference[];
    private readonly typeActivitiesObservable: Observable<CadorReference[]>;

    private documentTypes: CadorReference[];
    private readonly documentTypesObservable: Observable<CadorReference[]>;

    private permisSejour: CadorReference[];
    private readonly permisSejourObservable: Observable<CadorReference[]>;

    constructor(
        private httpClient: HttpClient,
        private growlService: GrowlBrokerService
    ) {
        this.initiatorsObservable = this.httpClient
            .get<CadorReference[]>(`${REFCADOR_BASE_URL}initiators`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les initiateurs'
                );
                return of<CadorReference[]>([]);
            }),
                  tap(initiators => {
                      this.initiators = initiators;
                  }),
                  shareReplay(1)
            );

        this.projectTypesObservable = this.httpClient
            .get<CadorReference[]>(`${REFCADOR_BASE_URL}projectTypes`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les types de projet'
                );
                return of<CadorReference[]>([]);
            }),
                  tap(projectTypes => {
                      this.projectTypes = projectTypes;
                  }),
                  shareReplay(1)
            );

        this.relationsObservable = this.httpClient
            .get<CadorReference[]>(`${REFCADOR_BASE_URL}relations`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les relations'
                );
                return of<CadorReference[]>([]);
            }),
                  tap(relations => {
                      this.relations = relations;
                  }),
                  shareReplay(1)
            );

        this.contactPreferencesObservable = this.httpClient
            .get<CadorReference[]>(`${REFCADOR_BASE_URL}contactPreferences`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les préférences de contact'
                );
                return of<CadorReference[]>([]);
            }),
                  tap(contactPreferences => {
                      this.contactPreferences = contactPreferences;
                  }),
                  shareReplay(1)
            );

        this.situationsObservable = this.httpClient
            .get<CadorReference[]>(`${REFCADOR_BASE_URL}situations`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les situations'
                );
                return of<CadorReference[]>([]);
            }),
                  tap(situations => {
                      this.situations = situations;
                  }),
                  shareReplay(1)
            );

        this.levelOfEducationsObservable = this.httpClient
            .get<CadorReference[]>(`${REFCADOR_BASE_URL}levelOfEducations`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les niveaux d\'éducation'
                );
                return of<CadorReference[]>([]);
            }),
                  tap(levelOfEducations => {
                      this.levelOfEducations = levelOfEducations;
                  }),
                  shareReplay(1)
            );

        this.typeActivitiesObservable = this.httpClient
            .get<CadorReference[]>(`${REFCADOR_BASE_URL}typeActivities`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les typeActivities'
                );
                return of<CadorReference[]>([]);
            }),
                  tap(typeActivities => {
                      this.typeActivities = typeActivities;
                  }),
                  shareReplay(1)
            );

        this.documentTypesObservable = this.httpClient
            .get<CadorReference[]>(`${REFCADOR_BASE_URL}documentTypes`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les documentTypes'
                );
                return of<CadorReference[]>([]);
            }),
                  tap(documentTypes => {
                      this.documentTypes = documentTypes;
                  }),
                  shareReplay(1)
            );

        this.permisSejourObservable = this.httpClient
            .get<CadorReference[]>(`${REFCADOR_BASE_URL}permisSejour`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les permisSejour'
                );
                return of<CadorReference[]>([]);
            }),
                  tap(permisSejour => {
                      this.permisSejour = permisSejour;
                  }),
                  shareReplay(1)
            );

        this.libellesObservable = this.httpClient
            .get<LibelleConfigurable[]>(`${REFCADOR_BASE_URL}generic/allLibelle`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les libelles'
                );
                return of<LibelleConfigurable[]>([]);
            }),
                  tap(libelles => {
                      this.libelles = libelles;
                  }),
                  shareReplay(1)
            );

    }

    getInitiators(): Observable<CadorReference[]> {
        return this.initiatorsObservable;
    }

    getInitiatorsSync(): CadorReference[] {
        if (!this.initiators) {
            throw new Error('Initiators not initialized.');
        }

        return this.initiators;
    }

    getProjectTypes(): Observable<CadorReference[]> {
        return this.projectTypesObservable;
    }

    getProjectTypesSync(): CadorReference[] {
        if (!this.projectTypes) {
            throw new Error('ProjectTypes not initialized.');
        }

        return this.projectTypes;
    }

    getContactPreferences(): Observable<CadorReference[]> {
        return this.contactPreferencesObservable;
    }

    getContactPreferencesSync(): CadorReference[] {
        if (!this.contactPreferences) {
            throw new Error('ContactPreferences not initialized.');
        }

        return this.contactPreferences;
    }

    getRelations(): Observable<CadorReference[]> {
        return this.relationsObservable;
    }

    getRelationsSync(): CadorReference[] {
        if (!this.relations) {
            throw new Error('Relations not initialized.');
        }

        return this.relations;
    }

    getSituations(): Observable<CadorReference[]> {
        return this.situationsObservable;
    }

    getSituationsSync(): CadorReference[] {
        if (!this.situations) {
            throw new Error('Situations not initialized.');
        }

        return this.situations;
    }

    getLevelOfEducations(): Observable<CadorReference[]> {
        return this.levelOfEducationsObservable;
    }

    getLevelOfEducationsSync(): CadorReference[] {
        if (!this.levelOfEducations) {
            throw new Error('LevelOfEducations not initialized.');
        }

        return this.levelOfEducations;
    }

    getLibelles(): Observable<LibelleConfigurable[]> {
        return this.libellesObservable;
    }

    getLibellesSync(): LibelleConfigurable[] {
        if (!this.libelles) {
            throw new Error('Libelles not initialized.');
        }

        return this.libelles;
    }

    getTypeActivities(): Observable<CadorReference[]> {
        return this.typeActivitiesObservable;
    }

    getTypeActivitiesSync(): CadorReference[] {
        if (!this.libelles) {
            throw new Error('typeActivities not initialized.');
        }

        return this.typeActivities;
    }

    getDocumentTypes(): Observable<CadorReference[]> {
        return this.documentTypesObservable;
    }

    getDocumentTypesSync(): CadorReference[] {
        if (!this.documentTypes) {
            throw new Error('documentTypes not initialized.');
        }
        return this.documentTypes;
    }

    getPermisSejour(): Observable<CadorReference[]> {
        return this.permisSejourObservable;
    }

    getPermisSejourSync(): CadorReference[] {
        if (!this.permisSejour) {
            throw new Error('documentTypes not initialized.');
        }
        return this.permisSejour;
    }

}
